import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";

import type { OnboardingProfileLike, OnboardingVoiceLike } from "@/lib/brand-onboarding-display";

type RouterOutputs = inferRouterOutputs<AppRouter>;

export type BrandProfile = RouterOutputs["brand"]["getBrandProfile"];
export type BrandVoice = RouterOutputs["brand"]["getBrandVoice"];
export type BrandVisualStyle = RouterOutputs["brand"]["getVisualStyle"];
export type EditableBrandFields = Record<string, string | string[]>;
export type ConfirmReviewStep = 0 | 1 | 2;
export type VoiceFieldKey =
  | "tone"
  | "voice"
  | "vocabularyStyle"
  | "sentenceComplexity"
  | "contentDnaHumorLevel"
  | "useOfEmoji"
  | "culturalReferences";

export type ValueProp = {
  id: string;
  title: string;
  description: string;
  text: string;
};

export type AudienceSegment = {
  title: string;
  description?: string;
};

export const PROFILE_FIELDS = {
  whatYouDo: "coreIdentity.uniqueSellingProposition",
  primaryAudienceSegments: "targetAudience.primaryAudienceSegments",
  secondaryAudienceSegments: "targetAudience.secondaryAudienceSegments",
  painPoints: "targetAudience.painPoints",
  keyDifferentiators: "coreIdentity.keyDifferentiators",
  perceivedPositioning: "positioningMarketContext.perceivedPositioning",
} as const;

export const VOICE_FIELDS = {
  tone: "brandVoiceCommunicationStyle.tone",
  voice: "brandVoiceCommunicationStyle.voice",
  vocabularyStyle: "brandVoiceCommunicationStyle.vocabularyStyle",
  sentenceComplexity: "brandVoiceCommunicationStyle.sentenceComplexity",
  contentDnaHumorLevel: "brandVoiceCommunicationStyle.contentDnaHumorLevel",
  useOfEmoji: "brandVoiceCommunicationStyle.useOfEmoji",
  culturalReferences: "brandVoiceCommunicationStyle.culturalReferences",
  wordsToAvoid: "brandVoiceCommunicationStyle.wordsToAvoid",
  wordsToEmphasize: "brandVoiceCommunicationStyle.wordsToEmphasize",
} as const;

export const CONTENT_DNA_FIELDS = {
  contentAngles: "brandVoiceCommunicationStyle.contentAngles",
} as const;

export const VISUAL_FIELDS = {
  colorPalette: "visualIdentitySnapshot.colorPalette",
  logoUrl: "visualIdentitySnapshot.logoUrl",
} as const;

export const getVisualIdentityFieldVisibility = (
  visual: Pick<BrandVisualStyle, "logoUrl" | "colorPalette">,
) => {
  const hasLogo = Boolean(visual.logoUrl?.trim());
  const hasPalette = (visual.colorPalette ?? []).filter(Boolean).length > 0;

  return {
    hasLogo,
    hasPalette,
    hasCompleteVisualIdentity: hasLogo && hasPalette,
    showLogoInput: !hasLogo,
    showPaletteEditor: !hasPalette,
    showPaletteSwatches: hasPalette,
  };
};

const trim = (value: string | null | undefined): string | null => {
  const next = value?.trim();
  return next ? next : null;
};

const uniqueStrings = (values: Array<string | null | undefined>): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const next = trim(value);
    if (!next) continue;

    const key = next.toLocaleLowerCase();
    if (seen.has(key)) continue;

    seen.add(key);
    result.push(next);
  }

  return result;
};

const sentenceCaseStart = (value: string): string =>
  value ? `${value[0]?.toLocaleUpperCase() ?? ""}${value.slice(1)}` : value;

export const splitValuePropText = (value: string): Pick<ValueProp, "title" | "description"> => {
  const [title, ...descriptionParts] = value.split(/\s+[—–-]\s+/);
  const description = sentenceCaseStart(descriptionParts.join(" — ").trim());

  if (!description) {
    return { title: value, description: "" };
  }

  return { title: (title ?? value).trim(), description };
};

export const formatValuePropText = (item: Pick<ValueProp, "title" | "description">): string => {
  if (item.title && item.description) {
    return `${item.title} — ${item.description}`;
  }

  return item.description || item.title;
};

export const deriveValueProps = (input: {
  profile: Pick<
    OnboardingProfileLike,
    "uniqueSellingProposition" | "tagline" | "marketCategory" | "keyDifferentiators"
  >;
}): ValueProp[] => {
  const values = (input.profile.keyDifferentiators ?? [])
    .map((value) => trim(value))
    .filter((value): value is string => Boolean(value));

  if (!values.length) {
    const legacyUsp = trim(input.profile.uniqueSellingProposition);
    if (legacyUsp) values.push(legacyUsp);
  }

  const items: ValueProp[] = [];
  for (const [index, value] of values.entries()) {
    items.push({
      id: `value-prop-${index}`,
      text: value,
      ...splitValuePropText(value),
    });
  }

  return items;
};

export const buildValuePropFields = (items: ValueProp[]): EditableBrandFields => {
  const cleaned = items
    .map((item) => item.text.trim() || formatValuePropText(item).trim())
    .filter(Boolean);

  return {
    [PROFILE_FIELDS.whatYouDo]: "",
    [PROFILE_FIELDS.keyDifferentiators]: cleaned,
  };
};

export const buildAudienceFields = (items: AudienceSegment[]): EditableBrandFields => ({
  [PROFILE_FIELDS.primaryAudienceSegments]: items.map((item) => item.title.trim()).filter(Boolean),
  [PROFILE_FIELDS.secondaryAudienceSegments]: [],
});

export const getPopulatedVoiceEntries = (
  voice: Pick<
    OnboardingVoiceLike,
    VoiceFieldKey | "wordsToEmphasize" | "useOfEmoji" | "contentDnaPrimaryStyle" | "socialMechanics"
  >,
  labels: Record<VoiceFieldKey, string>,
): Array<{
  key: VoiceFieldKey | "wordsToEmphasize" | "contentDnaPrimaryStyle" | "socialMechanics";
  label: string;
  value: string;
}> => [
  ...(
    [
      "tone",
      "voice",
      "vocabularyStyle",
      "sentenceComplexity",
      "contentDnaHumorLevel",
      "useOfEmoji",
      "culturalReferences",
    ] as const
  )
    .map((key) => ({ key, label: labels[key], value: voice[key] ?? "" }))
    .filter((entry) => entry.value.trim().length > 0),
  ...(voice.wordsToEmphasize ?? [])
    .map((value) => value.trim())
    .filter(Boolean)
    .map((value) => ({ key: "wordsToEmphasize" as const, label: "Voice", value })),
  ...uniqueStrings([voice.contentDnaPrimaryStyle]).map((value) => ({
    key: "contentDnaPrimaryStyle" as const,
    label: "Content DNA",
    value,
  })),
  ...uniqueStrings(voice.socialMechanics ?? []).map((value) => ({
    key: "socialMechanics" as const,
    label: "Content DNA",
    value,
  })),
];
